import { useState } from "react"
import { Radio, RadioGroup } from "react-aria-components"
import { registerAdornmentHandler } from "../../../../data-interactive/handlers/adornment-handler"
import { useTileModelContext } from "../../../../hooks/use-tile-model-context"
import { logMessageWithReplacement } from "../../../../lib/log-message"
import { updateTileNotification } from "../../../../models/tiles/tile-notifications"
import { t } from "../../../../utilities/translation/translate"
import { PaletteCheckbox } from "../../../palette-checkbox"
import { useGraphContentModelContext } from "../../hooks/use-graph-content-model-context"
import { useGraphDataConfigurationContext } from "../../hooks/use-graph-data-configuration-context"
import { registerAdornmentComponentInfo } from "../adornment-component-info"
import {
  exportAdornmentBase, getAdornmentContentInfo, IAdornmentExporterOptions, registerAdornmentContentInfo
} from "../adornment-content-info"
import { IAdornmentModel } from "../adornment-models"
import { CountAdornment } from "./count-adornment-component"
import { countAdornmentHandler } from "./count-adornment-handler"
import { CountAdornmentModel, ICountAdornmentModel, isCountAdornment, PercentType } from "./count-adornment-model"
import { kCountClass, kCountLabelKey, kCountPrefix, kCountType, kPercentLabelKey } from "./count-adornment-types"

const Controls = () => {
  const graphModel = useGraphContentModelContext()
  const dataConfig = useGraphDataConfigurationContext()
  const { tile } = useTileModelContext()
  const adornmentsStore = graphModel.adornmentsStore
  const existingAdornment = adornmentsStore.findAdornmentOfType<ICountAdornmentModel>(kCountType)
  const leftBottomCategoricalAttrCount = dataConfig?.leftBottomCategoricalAttrCount ?? 0
  const shouldShowPercentOption = leftBottomCategoricalAttrCount || adornmentsStore.subPlotsHaveRegions ||
                                  graphModel.plotType === "binnedDotPlot"
  const shouldShowPercentTypeOptions = leftBottomCategoricalAttrCount > 1
  const [enablePercentOptions, setEnablePercentOptions] = useState(existingAdornment?.showPercent)
  const [percentTypeValue, setPercentTypeValue] = useState(
    existingAdornment && isCountAdornment(existingAdornment) ? existingAdornment.percentType : "row"
  )

  const handleShowPercent = (adornment: ICountAdornmentModel, checked: boolean) => {
    adornment.setShowPercent(checked)
    setEnablePercentOptions(checked)
  }

  const handleSetting = (checked: boolean, checkBoxType: string) => {
    const existingCountAdornment = adornmentsStore.findAdornmentOfType<ICountAdornmentModel>(kCountType)
    const componentContentInfo = getAdornmentContentInfo(kCountType)
    const adornment = existingCountAdornment ?? componentContentInfo.modelClass.create() as ICountAdornmentModel
    const isCount = checkBoxType === "count"
    const undoAddKey = isCount ? "DG.Undo.graph.showCount" : "DG.Undo.graph.showPercent"
    const redoAddKey = isCount ? "DG.Redo.graph.showCount" : "DG.Redo.graph.showPercent"
    const undoRemoveKey = isCount ? "DG.Undo.graph.hideCount" : "DG.Undo.graph.hidePercent"
    const redoRemoveKey = isCount ? "DG.Redo.graph.hideCount" : "DG.Redo.graph.hidePercent"

    const setShowAdornment = isCount
      ? () => adornment.setShowCount(checked)
      : () => handleShowPercent(adornment, checked)

    // V2 emits `toggle plotted Count` / `toggle plotted Percent` from
    // apps/dg/components/graph/plots/plot_model.js (togglePlottedCount(iWhat) ~:465).
    // Op string is `'toggle plotted ' + iWhat` where iWhat is `'Count'` or `'Percent'`.
    const notificationOp = isCount ? "toggle plotted Count" : "toggle plotted Percent"
    const notify = tile
      ? () => updateTileNotification(notificationOp, { isChecked: checked }, tile)
      : undefined

    if (checked) {
      graphModel.applyModelChange(() => {
        adornmentsStore.addAdornment(adornment, graphModel.getUpdateCategoriesOptions())
        setShowAdornment()
      }, {
        notify,
        undoStringKey: undoAddKey,
        redoStringKey: redoAddKey,
        log: logMessageWithReplacement("Show %@", {adornmentType: isCount ? "count" : "percent"})
      })
    } else {
      graphModel.applyModelChange(() => adornmentsStore.updateAdornment(setShowAdornment), {
        notify,
        undoStringKey: undoRemoveKey,
        redoStringKey: redoRemoveKey,
        log: logMessageWithReplacement("Hide %@", {adornmentType: isCount ? "count" : "percent"})
      })
    }
  }

  const handlePercentTypeSetting = (percentType: string) => {
    existingAdornment?.setPercentType(percentType as PercentType)
    setPercentTypeValue(percentType as PercentType)
  }

  return (
    <>
      <PaletteCheckbox
        data-testid={`adornment-checkbox-${kCountClass}-count`}
        isSelected={existingAdornment?.showCount}
        onChange={checked => handleSetting(checked, "count")}
      >
        {t(kCountLabelKey)}
      </PaletteCheckbox>
      {shouldShowPercentOption &&
        <>
          <PaletteCheckbox
            data-testid={`adornment-checkbox-${kCountClass}-percent`}
            isSelected={existingAdornment?.showPercent}
            onChange={checked => handleSetting(checked, "percent")}
          >
            {t(kPercentLabelKey)}
          </PaletteCheckbox>
          {shouldShowPercentTypeOptions &&
            <div
              className="sub-options percent-type"
              data-testid="adornment-percent-type-options"
            >
              <RadioGroup
                aria-label={t(kPercentLabelKey)}
                isDisabled={!enablePercentOptions}
                value={percentTypeValue}
                onChange={handlePercentTypeSetting}
              >
                <Radio value="row">
                  {({isSelected}) => (
                    <>
                      <span className={`radio-indicator${isSelected ? " selected" : ""}`} />
                      {t("DG.Inspector.graphRow")}
                    </>
                  )}
                </Radio>
                <Radio value="column">
                  {({isSelected}) => (
                    <>
                      <span className={`radio-indicator${isSelected ? " selected" : ""}`} />
                      {t("DG.Inspector.graphColumn")}
                    </>
                  )}
                </Radio>
                <Radio value="cell">
                  {({isSelected}) => (
                    <>
                      <span className={`radio-indicator${isSelected ? " selected" : ""}`} />
                      {t("DG.Inspector.graphCell")}
                    </>
                  )}
                </Radio>
              </RadioGroup>
            </div>
          }
        </>
      }
    </>
  )
}

const v2PercentTypes: Record<string, number> = {
  cell: 0,
  row: 1,
  column: 2
}

registerAdornmentContentInfo({
  type: kCountType,
  plots: ["casePlot", "dotChart", "dotPlot", "scatterPlot"],
  prefix: kCountPrefix,
  modelClass: CountAdornmentModel,
  exporter: (model: IAdornmentModel, options: IAdornmentExporterOptions) => {
    const adornment = isCountAdornment(model) ? model : undefined
    // in v2 file format, showing movable values "turns off" the count adornment
    const isVisible = model.isVisible && !options.isShowingMovableValues
    return adornment
            ? {
                plottedCount: {
                  ...exportAdornmentBase(model, { ...options, isVisible }),
                  isShowingCount: adornment.showCount && !options.isShowingMovableValues,
                  isShowingPercent: adornment.showPercent && !options.isShowingMovableValues,
                  percentKind: v2PercentTypes[adornment.percentType]
                }
              }
            : undefined
  }
})

registerAdornmentComponentInfo({
  adornmentEltClass: kCountClass,
  Component: CountAdornment,
  Controls,
  labelKey: kCountLabelKey,
  order: 10,
  type: kCountType
})

registerAdornmentHandler(kCountType, countAdornmentHandler, "Percent")
